'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return (
        <html lang="en">
            <body
                style={{
                    margin: 0,
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    background: 'linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%)',
                    color: '#1a1a2e',
                }}
            >
                <div
                    style={{
                        textAlign: 'center',
                        maxWidth: 480,
                        padding: '48px 32px',
                        background: '#fff',
                        borderRadius: 20,
                        boxShadow:
                            '0 4px 24px rgba(80, 70, 120, 0.08), 0 1px 3px rgba(0,0,0,0.04)',
                    }}
                >
                    {/* Animated warning icon */}
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            margin: '0 auto 24px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'pulse-icon 2s ease-in-out infinite',
                        }}
                    >
                        <svg
                            width="36"
                            height="36"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>

                    <h1
                        style={{
                            fontSize: 24,
                            fontWeight: 700,
                            margin: '0 0 12px',
                            color: '#1a1a2e',
                        }}
                    >
                        Oops! Something went wrong
                    </h1>

                    <p
                        style={{
                            fontSize: 15,
                            lineHeight: 1.6,
                            color: '#64748b',
                            margin: '0 0 32px',
                        }}
                    >
                        Don&apos;t worry — this error has been automatically reported and our
                        team is already looking into it. We&apos;ll have things fixed
                        shortly.
                    </p>

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                        <button
                            onClick={() => reset()}
                            style={{
                                padding: '10px 24px',
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#fff',
                                background:
                                    'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                                border: 'none',
                                borderRadius: 10,
                                cursor: 'pointer',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow =
                                    '0 4px 16px rgba(99, 102, 241, 0.4)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow =
                                    '0 2px 8px rgba(99, 102, 241, 0.3)';
                            }}
                        >
                            Try Again
                        </button>

                        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                        <a
                            href="/"
                            style={{
                                padding: '10px 24px',
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#6366f1',
                                background: '#f0f0ff',
                                border: 'none',
                                borderRadius: 10,
                                cursor: 'pointer',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                transition: 'background 0.15s',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#e8e8ff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#f0f0ff';
                            }}
                        >
                            Go Home
                        </a>
                    </div>

                    {process.env.NODE_ENV === 'development' && error?.message && (
                        <details
                            style={{
                                marginTop: 24,
                                textAlign: 'left',
                                padding: '12px 16px',
                                background: '#fef2f2',
                                borderRadius: 10,
                                fontSize: 13,
                                color: '#991b1b',
                            }}
                        >
                            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                                Error Details (dev only)
                            </summary>
                            <pre
                                style={{
                                    marginTop: 8,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {error.message}
                            </pre>
                        </details>
                    )}
                </div>

                <style>{`
                    @keyframes pulse-icon {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                    }
                `}</style>
            </body>
        </html>
    );
}
